import { Pencil, Plus, ShieldCheck, Trash2, UserCog } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { type AccessGrant, useAccessGrants, useRemoveAccessGrant } from "@/hooks/use-access";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useSession } from "@/lib/auth-client";
import { ACCESS_LEVEL_OPTIONS } from "@/lib/enums";
import { formatDate, formatDateTime } from "@/lib/format";
import { AccessLevelBadge } from "./AccessLevelBadge";
import { AddPersonDialog } from "./AddPersonDialog";
import { EditAccessDialog } from "./EditAccessDialog";

/** What each level sees, in the words the admin would use to explain it. */
function LevelsExplainer() {
  return (
    <ul className="grid gap-2 text-sm sm:grid-cols-3">
      {ACCESS_LEVEL_OPTIONS.map((option) => (
        <li key={option.value} className="rounded-md border border-border px-3 py-2">
          <span className="font-medium">{option.label}</span>
          <span className="text-muted-foreground"> — {option.description}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Settings -> Access: who may sign in to the dashboard (and use the Claude
 * connector), and how much detail they see.
 */
export function AccessTable() {
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const { data: session } = useSession();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AccessGrant | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AccessGrant | null>(null);

  const { data, isLoading, isError, error, refetch } = useAccessGrants(
    grid.query,
    grid.pageRequest,
  );
  const remove = useRemoveAccessGrant();
  const grants = data?.data ?? [];
  const ownEmail = session?.user?.email?.toLowerCase();

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search by name or email..."
        className="w-full sm:w-auto sm:max-w-sm sm:flex-1"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={ACCESS_LEVEL_OPTIONS}
        allOption={{ label: "All levels" }}
        placeholder="All levels"
        className="w-[150px]"
        aria-label="Filter by access level"
      />
      <Button size="sm" className="ml-auto" onClick={() => setAddOpen(true)}>
        <Plus className="mr-1 h-4 w-4" />
        Add person
      </Button>
    </div>
  );

  const dialogs = (
    <>
      <AddPersonDialog open={addOpen} onOpenChange={setAddOpen} />
      {editTarget && (
        <EditAccessDialog
          key={editTarget.id}
          open
          onOpenChange={(open) => !open && setEditTarget(null)}
          grant={editTarget}
        />
      )}
      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Remove access"
        description={`${removeTarget?.name ?? removeTarget?.email ?? "This person"} will be signed out now and will not be able to sign in again, here or through Claude.`}
        confirmLabel="Remove access"
        variant="destructive"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget, { onSuccess: () => setRemoveTarget(null) });
        }}
      />
    </>
  );

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Only the people listed here can sign in to the dashboard or use it through Claude.
        </p>
        <LevelsExplainer />
      </div>

      {toolbar}

      {isError ? (
        <ErrorState message={error?.message ?? "Failed to load access"} onRetry={() => refetch()} />
      ) : (
        <>
          <DataTable
            headers={[
              { label: "Name" },
              { label: "Email", className: "hidden md:table-cell", sortKey: "email" },
              { label: "Access level", sortKey: "level" },
              { label: "Title", className: "hidden md:table-cell" },
              { label: "Last signed in", className: "hidden lg:table-cell" },
              { label: "Added", className: "hidden lg:table-cell", sortKey: "createdAt" },
              { label: "", className: "w-12" },
            ]}
            sort={grid.sort}
            sortDir={grid.sortDir}
            onSort={grid.toggleSort}
            loading={isLoading}
            empty={
              <EmptyState
                icon={ShieldCheck}
                title={
                  grid.query.search || grid.query.status ? "No one matches" : "No one has access"
                }
                description={
                  grid.query.search || grid.query.status
                    ? "Try a different search or level."
                    : "Add a person to let them sign in."
                }
              />
            }
          >
            {grants.map((grant) => {
              const isSelf = grant.email === ownEmail;
              return (
                <tr
                  key={grant.id}
                  className="border-b border-border transition-colors last:border-0 hover:bg-muted/30"
                >
                  <td className="px-4 py-3 font-medium">
                    {grant.name ?? <span className="text-muted-foreground">Not signed in yet</span>}
                    {isSelf && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                    {/* Phones drop the Email column, so the address rides under the name. */}
                    <span className="block break-all text-xs font-normal text-muted-foreground md:hidden">
                      {grant.email}
                    </span>
                  </td>
                  <td className="hidden max-w-[16rem] truncate px-4 py-3 text-muted-foreground md:table-cell">
                    {grant.email}
                  </td>
                  <td className="px-4 py-3">
                    <AccessLevelBadge level={grant.level} />
                  </td>
                  <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                    {grant.title ?? "—"}
                  </td>
                  <td className="hidden whitespace-nowrap px-4 py-3 text-muted-foreground lg:table-cell">
                    {grant.lastSignInAt ? formatDateTime(grant.lastSignInAt) : "Never"}
                  </td>
                  <td className="hidden whitespace-nowrap px-4 py-3 text-muted-foreground lg:table-cell">
                    {formatDate(grant.createdAt)}
                    {grant.addedByName && (
                      <span className="block text-xs">by {grant.addedByName}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowActions
                      label={`Actions for ${grant.email}`}
                      actions={[
                        // Your own level and access are not yours to change:
                        // the server refuses it, so the menu does not offer it.
                        ...(isSelf
                          ? []
                          : [
                              {
                                label: "Change access level",
                                icon: UserCog,
                                onSelect: () => setEditTarget(grant),
                              },
                            ]),
                        { label: "Edit title", icon: Pencil, onSelect: () => setEditTarget(grant) },
                        ...(isSelf
                          ? []
                          : [
                              {
                                label: "Remove access",
                                icon: Trash2,
                                destructive: true,
                                onSelect: () => setRemoveTarget(grant),
                              },
                            ]),
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </DataTable>

          <CursorPagination
            total={data?.total ?? 0}
            page={grid.page}
            limit={DEFAULT_LIMIT}
            hasNext={data?.hasNext ?? false}
            hasPrev={data?.hasPrev ?? false}
            onNext={() => grid.goNext(data?.nextCursor ?? null)}
            onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
            loading={isLoading}
            itemLabel="people"
          />
        </>
      )}

      {dialogs}
    </div>
  );
}
